import { useCallback } from 'react';
import { useCookies } from 'react-cookie';
import { setErrorMessages, setIsSystemErrorOpen, useAppContext } from '../context/app-context';
import { setIsRedirectDialogOpen } from '../context/app-context/app-context-actions';
import { ErrorResponse, RegionalCodesList } from '../global-types';
import { cleanAccountCookies, getMonolithUrl, handleApiErrors, removeTokens } from '../utils';
import { isAnyOfRegions } from '../utils/utility';

type OnSystemErrorOptions = {
  redirect?: boolean;
  errorCallback?: () => void;
};

export type OnSystemError = (errors: ErrorResponse, options?: OnSystemErrorOptions) => void;

type UseSytemErrorHandlingMethods = {
  onSystemError: OnSystemError;
};

/**
 * Collection of custom hooks that return functions that handle system and API errors.
 */
export const useSystemErrorHandling = (): UseSytemErrorHandlingMethods => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const removeCookie = useCookies()[2];
  const { appDispatch } = useAppContext();

  const onSystemError: OnSystemError = useCallback(
    (errors: ErrorResponse, options: OnSystemErrorOptions = { redirect: false }) => {
      const { redirect, errorCallback }: OnSystemErrorOptions = options;
      const onAccountError = () => {
        cleanAccountCookies(removeCookie as any);
        removeTokens();
      };

      const callback = () => {
        setErrorMessages(
          appDispatch,
          errors?.data?.errors?.map((error) => error.description),
        );

        if (redirect) {
          let queryString = '';

          if (isAnyOfRegions([RegionalCodesList.uk])) {
            queryString = '?consent=false';
          }

          if (isAnyOfRegions([RegionalCodesList.ce])) {
            queryString = '?consent=false';
          }

          const redirectUrl = `${getMonolithUrl()}${queryString}`;

          setIsRedirectDialogOpen(appDispatch, { isDialogOpen: true, redirectUrl });
        } else {
          setIsSystemErrorOpen(appDispatch, true);
        }

        if (errorCallback) {
          errorCallback();
        }
      };

      handleApiErrors(errors, onAccountError, callback);
    },
    [appDispatch, removeCookie],
  );

  return { onSystemError };
};
